"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, ShieldCheck, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
interface ProcessStep {
  step: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  keyTakeaway: string;
}
const steps: ProcessStep[] = [{
  step: "01",
  title: "Understand your needs",
  description: "We start with a conversation about your insurance needs, priorities, questions, and goals.",
  icon: Compass,
  keyTakeaway: "Clear understanding of your priorities"
}, {
  step: "02",
  title: "Explore available options",
  description: "We review available life insurance and annuity options from multiple insurance carriers and explain how they work.",
  icon: Sparkles,
  keyTakeaway: "Clear comparison of available options"
}, {
  step: "03",
  title: "Make an informed decision",
  description: "You review the information, ask questions, and decide what fits your needs and budget.",
  icon: ShieldCheck,
  keyTakeaway: "An informed decision based on your needs"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="how-it-works" data-nav="light" className="relative bg-background py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-12">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-chart-2/30 bg-chart-2/10 px-3.5 py-1 text-xs font-semibold tracking-wide text-chart-2 uppercase">
            How the process works.
          </Badge>

          <h2 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-[2.75rem] md:leading-[1.22]">
            A clear path to protecting what matters most
          </h2>

          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="size-1 rounded-full bg-chart-2" />
            <div className="h-px w-16 bg-border" />
          </div>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            We replace pressure with calm, independent education, comparing your
            options clearly. Here is how we help you find the right insurance and
            retirement strategy.
          </p>
        </div>

        {/* 3-Column Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {steps.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.step} data-index={i} variants={cardVariants} className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-chart-2/40 hover:shadow-md sm:p-7">
                <div>
                  {/* Top Step Header */}
                  <div className="flex items-center justify-between pb-6">
                    <span className="font-default text-2xl font-bold tracking-tight text-chart-2">
                      {item.step}
                    </span>
                    <div className="flex size-11 items-center justify-center rounded-lg bg-muted text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-5" />
                    </div>
                  </div>

                  {/* Step Title */}
                  <h3 className="font-default text-xl font-semibold tracking-tight text-foreground">
                    {item.title}
                  </h3>

                  {/* Step Description */}
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.description}
                  </p>
                </div>

                {/* Key Takeaway / Plinth Footer */}
                <div className="mt-8 border-t border-border pt-4">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Outcome
                  </span>
                  <p className="mt-1 font-default text-sm font-medium text-foreground">
                    {item.keyTakeaway}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Supportive Action Callout */}
        <div className="mt-14 flex flex-col items-center justify-center rounded-xl border border-border bg-muted/40 p-6 text-center sm:px-8 sm:py-7">
          <div className="max-w-2xl text-center">
            <h4 className="font-default text-base font-semibold text-foreground sm:text-lg">
              Ready to review your current policies or retirement plan?
            </h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Consultations are educational, confidential, and completely free of
              obligation.
            </p>
          </div>
        </div>
      </div>
    </section>;
}